import { z } from 'zod';

/**
 * Обязательная причина: одно правило на все места, где человек обязан
 * объяснить решение — отказ руководителя, возврат на доработку, отмена и
 * отказ исполнителя, невыполненное задание.
 *
 * Порог был выписан пятью отдельными числами в контрактах и в сервисе.
 * Пять копий одного правила расходятся молча: правят одну, а отказ приходит
 * от другой, и человек читает про «не короче 10», уже написав двенадцать.
 */
export const REASON_MIN_LENGTH = 10;

/** Схема обязательной причины со своей формулировкой отказа. */
export const requiredReason = (message: string) =>
  z.string().trim().min(REASON_MIN_LENGTH, message);

/** Достаточна ли причина. Один предикат на сервер и на форму. */
export function isReasonSufficient(value: string | null | undefined): boolean {
  return (value ?? '').trim().length >= REASON_MIN_LENGTH;
}

export const RailUserProfileSchema = z.object({
  userId: z.string().uuid(),
  homeStationId: z.string().uuid().nullable(),
  // Stage 1-7: additive membership-based employment reference. Kept
  // optional/nullable through the Stage 1-7 additive gate. Stage 1-9 owns
  // the final NOT NULL cutover.
  organizationMembershipId: z.string().uuid().nullable().optional(),
});
export type RailUserProfile = z.infer<typeof RailUserProfileSchema>;

// ─── Stage 1-7 additive RAIL ownership contracts ──────────────────────────
// Every RAIL query root eventually resolves to an Organization + fixed RAIL
// product. Membership references coexist with legacy User ids until Stage
// 1-9. Fields are optional here so response projections keep working while
// consumers migrate.

const RailProductSchema = z.literal('RAIL');
export type RailProduct = z.infer<typeof RailProductSchema>;

export const RailOwnershipSchema = z.object({
  organizationId: z.string().uuid().nullable().optional(),
  product: RailProductSchema.nullable().optional(),
});
export type RailOwnership = z.infer<typeof RailOwnershipSchema>;

export const RailRouteOwnershipSchema = RailOwnershipSchema.extend({
  workerMembershipId: z.string().uuid().nullable().optional(),
});
export type RailRouteOwnership = z.infer<typeof RailRouteOwnershipSchema>;

export const RailWorkConsentOwnershipSchema = RailOwnershipSchema.extend({
  workerMembershipId: z.string().uuid().nullable().optional(),
  createdByMembershipId: z.string().uuid().nullable().optional(),
});
export type RailWorkConsentOwnership = z.infer<typeof RailWorkConsentOwnershipSchema>;

// RAIL provisioning contracts. The generic WorkSite identity lives in core.ts;
// these schemas preserve the deployed RailHub station/section API exactly.
export const WorkSiteKindSchema = z.enum(['STATION', 'SECTION']);
export type WorkSiteKind = z.infer<typeof WorkSiteKindSchema>;

export const WorkSiteSchema = z.object({
  id: z.string().uuid(),
  kind: WorkSiteKindSchema,
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
});
export type WorkSite = z.infer<typeof WorkSiteSchema>;

type WorkSiteLinkInput = {
  kind?: WorkSiteKind;
  stationId?: string;
  fromStationId?: string;
  toStationId?: string;
};

export const WORK_SITE_LINK_MESSAGES = {
  stationRequired: 'Станция обязательна при виде места работ STATION',
  sectionStationsOnlyForSection: 'Станции перегона указываются только при виде места работ SECTION',
  fromStationRequired: 'Станция начала перегона обязательна при виде места работ SECTION',
  toStationRequired: 'Станция конца перегона обязательна при виде места работ SECTION',
  stationsMustDiffer: 'Станции начала и конца перегона должны различаться',
  stationOnlyForStation: 'Станция указывается только при виде места работ STATION',
} as const;

const refineSectionStationsDiffer = (data: WorkSiteLinkInput, ctx: z.RefinementCtx): void => {
  if (data.fromStationId && data.toStationId && data.fromStationId === data.toStationId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['toStationId'],
      message: WORK_SITE_LINK_MESSAGES.stationsMustDiffer,
    });
  }
};

const refineWorkSiteLink = (data: WorkSiteLinkInput, ctx: z.RefinementCtx): void => {
  if (data.kind === 'STATION') {
    if (!data.stationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stationId'],
        message: WORK_SITE_LINK_MESSAGES.stationRequired,
      });
    }
    if (data.fromStationId || data.toStationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fromStationId'],
        message: WORK_SITE_LINK_MESSAGES.sectionStationsOnlyForSection,
      });
    }
  }
  if (data.kind === 'SECTION') {
    if (!data.fromStationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fromStationId'],
        message: WORK_SITE_LINK_MESSAGES.fromStationRequired,
      });
    }
    if (!data.toStationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toStationId'],
        message: WORK_SITE_LINK_MESSAGES.toStationRequired,
      });
    }
    refineSectionStationsDiffer(data, ctx);
    if (data.stationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stationId'],
        message: WORK_SITE_LINK_MESSAGES.stationOnlyForStation,
      });
    }
  }
};

export const CreateWorkSiteSchema = z.object({
  kind: WorkSiteKindSchema,
  code: z.string().min(1),
  name: z.string().min(1),
  stationId: z.string().uuid().optional(),
  fromStationId: z.string().uuid().optional(),
  toStationId: z.string().uuid().optional(),
}).superRefine(refineWorkSiteLink);
export type CreateWorkSiteDto = z.infer<typeof CreateWorkSiteSchema>;

export const UpdateWorkSiteSchema = z.object({
  code: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
  stationId: z.string().uuid().optional(),
  fromStationId: z.string().uuid().optional(),
  toStationId: z.string().uuid().optional(),
}).superRefine(refineSectionStationsDiffer);
export type UpdateWorkSiteDto = z.infer<typeof UpdateWorkSiteSchema>;

/**
 * Время маршрута считается по минутам отметки, а не по её миллисекундам.
 *
 * На демо одна поездка давала три разных числа: итог 1 ч 54 при 115 минутах,
 * интервал «+40 м» при 41 минуте, опоздание «+51 мин» при 50. Причина не в
 * разном округлении, а в том, ЧТО округляют: итог округлял один большой
 * интервал, таймлайн — каждый отрезок отдельно, и сумма округлений расходилась
 * с округлением суммы. Секунды отметок при этом никого не интересуют.
 *
 * Поэтому к минуте приводится САМА отметка, до всякой арифметики. Тогда все
 * разности целые, и сумма интервалов таймлайна равна итогу поездки не по
 * счастливому совпадению, а по построению.
 */
export function minuteOfTimestamp(value: Date | string): number {
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Math.floor(ms / 60_000);
}

/** Разница между двумя отметками в целых минутах. Одна на сервер и клиент. */
export function minutesBetweenTimestamps(from: Date | string, to: Date | string): number {
  return minuteOfTimestamp(to) - minuteOfTimestamp(from);
}

/**
 * Транспорт, у которого при оформлении обязателен номер единицы.
 *
 * Живёт в RAIL-подпакете, а не в корневом barrel: словарь транспорта —
 * железнодорожный, и экранам маршрутов незачем ради одной проверки тянуть
 * совместимостный barrel целиком.
 */
export function transportNeedsNumber(transport: string | null | undefined): boolean {
  return transport === 'TRAIN' || transport === 'RAILCAR';
}

/**
 * Код станции по ЕСР — шесть цифр, как на станционном штампе («ст. Айса, код
 * 675939»). Маршрутный лист ФРУ № 12 показывает его рядом с названием станции
 * (ADR-008 §5).
 *
 * Правило формы живёт здесь, в одном месте на форму справочника, на схему
 * запроса и на ограничение в базе. Три копии «шести цифр» разошлись бы молча:
 * правят одну, а отказ приходит от другой.
 *
 * QR-код станции этим кодом НЕ заменяется (решение владельца В7): код ЕСР
 * публичен, и QR с «675939» любой напечатал бы дома.
 */
export const ESR_CODE_LENGTH = 6;
export const ESR_CODE_PATTERN = /^[0-9]{6}$/;

/** Годится ли строка как код ЕСР. Один предикат на сервер и на форму. */
export function isEsrCode(value: string | null | undefined): boolean {
  return ESR_CODE_PATTERN.test((value ?? '').trim());
}

/**
 * Схема кода ЕСР со своей формулировкой отказа.
 *
 * Отказ назван словами и С ПРИМЕРОМ: «шесть цифр» без образца человек у
 * справочника читает как «шесть символов» и набирает «ст-675».
 *
 * Живёт в RAIL-подпакете, а не в корневом barrel: код станции — понятие
 * железнодорожное, и форме справочника незачем ради одной проверки тянуть
 * совместимостный barrel целиком.
 */
export const EsrCodeSchema = z
  .string()
  .trim()
  .regex(ESR_CODE_PATTERN, 'Код станции по ЕСР — ровно шесть цифр, например 675939');

// ─── ADR-008: маршрутный лист учёта разъездов за месяц (форма ФРУ № 12) ──────
//
// Issue #508, шаг 3 из 5 порядка работ. Здесь только ОБЩИЕ ПРАВИЛА и форма
// ответа: чем измеряется месяц листа, из чего состоят его шапка и строки, и
// какими причинами лист отказывает в сдаче.
//
// Сборка строк из поездок живёт на СЕРВЕРЕ (`apps/api/src/route-sheets/`), а не
// здесь: номер листа присваивает сервер при выдаче, и собрать шапку до этого
// нельзя — в отличие от офлайн-акта, который телефон собирает сам и который
// поэтому и живёт в общем коде (`signing.ts`). Второй сборщик на телефоне был
// бы вторым ответом на вопрос, что в листе написано.

/**
 * Смещение времени Казахстана от UTC. С 1 марта 2024 года вся страна живёт по
 * UTC+5.
 *
 * Лист измеряет месяц и печатает даты по ЭТОМУ времени (ADR-008 §6): работник
 * и бухгалтерия читают бумагу по местным часам, и поездка, выехавшая 1-го в
 * 03:00 по Астане, — поездка первого числа, хотя по UTC ещё 31-е.
 *
 * ТО ЖЕ ЧИСЛО стоит в `apps/api/src/work-orders/work-order-number.ts`
 * (`WORK_ORDER_NUMBER_UTC_OFFSET_HOURS`) — год номера наряда считается по нему
 * же. Две копии одного смещения разошлись бы молча; свести их в одно место
 * здесь нельзя, не тронув модуль номера наряда, то есть чужую задачу, поэтому
 * это записано находкой для реестра, а не поправлено заодно.
 */
export const KAZAKHSTAN_UTC_OFFSET_HOURS = 5;

/**
 * Календарный месяц листа — строка `YYYY-MM`.
 *
 * Строкой, а не датой: месяц — это не момент времени, и тип `date` заставил бы
 * каждый слой заново решать, в каком поясе его толковать. Тем же правилом
 * живёт колонка `RouteSheet.month`, и её форму держит CHECK в базе.
 */
export const ROUTE_SHEET_MONTH_PATTERN = /^[0-9]{4}-(?:0[1-9]|1[0-2])$/;

/** Годится ли строка как месяц листа. Один предикат на сервер и на форму. */
export function isRouteSheetMonth(value: string | null | undefined): boolean {
  return ROUTE_SHEET_MONTH_PATTERN.test((value ?? '').trim());
}

export const RouteSheetMonthSchema = z
  .string()
  .trim()
  .regex(ROUTE_SHEET_MONTH_PATTERN, 'Месяц листа — год и месяц, например 2026-09');

/** Момент, сдвинутый в местное время: дальше от него берутся год, месяц и час. */
function kazakhstanClock(value: Date | string): Date {
  const ms = (value instanceof Date ? value : new Date(value)).getTime();
  if (!Number.isFinite(ms)) throw new RangeError(`не удалось разобрать момент времени: ${String(value)}`);
  return new Date(ms + KAZAKHSTAN_UTC_OFFSET_HOURS * 3_600_000);
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Месяц листа, в который попадает момент выезда из приписки.
 *
 * Решение владельца В8: поездка, начатая 31-го и законченная 1-го, целиком
 * ложится в месяц ВЫЕЗДА. Поэтому месяц считается от одной отметки — выезда, —
 * а не от каждой строки: иначе один переезд оказался бы в одном листе, а
 * обратный в другом, и ни один из двух не сходился бы с бумагой.
 */
export function routeSheetMonthOf(value: Date | string): string {
  const local = kazakhstanClock(value);
  return `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}`;
}

/** Год месяца листа: `2026-09` → 2026. Нужен номеру листа. */
export function routeSheetMonthYear(month: string): number {
  return Number(month.slice(0, 4));
}

/** Дата строки листа по времени Казахстана — `YYYY-MM-DD`, как на бланке. */
export function routeSheetLocalDate(value: Date | string): string {
  const local = kazakhstanClock(value);
  return `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}`;
}

/**
 * Сдаётся ли лист раньше последнего дня своего месяца (решение владельца
 * 27.09.2026), по времени Казахстана.
 *
 * `daysLeft` — сколько дней месяца ещё впереди после сегодняшнего: 27-го в
 * тридцатидневном месяце это 3. NULL — месяц уже кончился, ещё не начался или
 * сегодня его последний день: тогда сдача ничего не отсекает.
 */
export function routeSheetEarlySubmission(
  month: string,
  now: Date | string,
): { daysLeft: number; lastDay: string } | null {
  if (routeSheetMonthOf(now) !== month) return null;
  const local = kazakhstanClock(now);
  const lastDate = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth() + 1, 0)).getUTCDate();
  const daysLeft = lastDate - local.getUTCDate();
  return daysLeft > 0 ? { daysLeft, lastDay: `${month}-${pad2(lastDate)}` } : null;
}

/** Время строки листа по времени Казахстана — `ЧЧ:ММ`. Секунды на бланке не стоят. */
export function routeSheetLocalTime(value: Date | string): string {
  const local = kazakhstanClock(value);
  return `${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}`;
}

/**
 * Отметка присутствия в графе «Выбыл» или «Прибыл» (ADR-008 §3).
 *
 * На бумаге здесь штамп станции и подпись дежурного; у нас — станция с кодом
 * ЕСР, время по серверу и способ отметки. Ручная отметка без QR идёт с
 * пометкой, КТО её подтвердил (решение владельца В4): без имени подтвердившего
 * она ничем не отличается от записанной со слов.
 */
export const RouteSheetMarkSchema = z.object({
  /** Дата и время по времени Казахстана — ровно так, как их печатает бланк. */
  date: z.string(),
  time: z.string(),
  /** Момент по UTC: из него собирается канон подписанного листа (issue #509). */
  occurredAt: z.string(),
  stationName: z.string().nullable(),
  /** Код станции по ЕСР. Пусто — в справочнике его не заполнили. */
  stationEsrCode: z.string().nullable(),
  /** № поезда этой графы. Пусто — прочерк: автомобиль, автомотриса, пешком. */
  trainNumber: z.string().nullable(),
  method: z.enum(['QR', 'MANUAL_FALLBACK']),
  /**
   * Кто подтвердил ручную отметку. Заполнено только у ручной и только после
   * подтверждения: неподтверждённая ручная отметка не даёт сдать лист, и
   * пометка об этом — не украшение, а основание отказа.
   */
  manualApprovedByName: z.string().nullable(),
  manualApprovedByEmployeeNumber: z.string().nullable(),
});
export type RouteSheetMark = z.infer<typeof RouteSheetMarkSchema>;

/**
 * Строка листа — ПЕРЕЕЗД между двумя соседними точками цепочки станций поездки
 * (ADR-008 §2, решение владельца В6).
 *
 * Поездка на одну станцию даёт две строки (туда и обратно), на две — три.
 * Наименование работ и итог стоят в строке ПРИБЫТИЯ на станцию работ; у строки
 * возврата их нет, и `returnToHome` говорит об этом прямо.
 *
 * ГОТОВОГО ТЕКСТА ЗДЕСЬ НЕТ намеренно — ни «возвращение к месту приписки», ни
 * «выполнено частично». Тем же правилом, что у поимённого отказа по готовности
 * бригады (`apps/api/src/work-orders/crew-readiness-refusal.ts`): собранное на
 * сервере русское предложение осталось бы русским на казахском интерфейсе, а
 * это объяснение системы, а не введённые человеком данные.
 */
export const RouteSheetLineSchema = z.object({
  /** Поездка, из которой собран переезд: по ней открывают карточку. */
  routeId: z.string(),
  /** Порядок в листе, с 1. Считается по времени выбытия (ADR-008 §6). */
  sequence: z.number().int(),
  departed: RouteSheetMarkSchema,
  arrived: RouteSheetMarkSchema,
  /** Наименование работ — то, что назначил мастер. Пусто у строки возврата. */
  workName: z.string().nullable(),
  /** Итог работы на станции прибытия и примечание к нему. */
  resultStatus: z.enum(['DONE', 'PARTIAL', 'NOT_DONE']).nullable(),
  resultComment: z.string().nullable(),
  /** Последняя строка поездки: возвращение к месту приписки. */
  returnToHome: z.boolean(),
});
export type RouteSheetLine = z.infer<typeof RouteSheetLineSchema>;

/**
 * Почему лист ещё нельзя сдать (ADR-008 §6, решение владельца В11).
 *
 * Кодами, а не фразами: язык выбирает тот, кто показывает. Причина называет
 * ПОЕЗДКУ — иначе человек с пятью выездами за месяц не знает, какой из них
 * разбирать.
 *
 * `ISSUE_MISSING` — единственная причина без поездки: она о самом листе.
 * Ездить без выдачи можно, сдать без выдачи нельзя.
 *
 * `ROUTE_NOT_FINISHED` и `ROUTE_NOT_CONFIRMED` разделены не для красоты:
 * первую закрывает работник (доехать, отчитаться), вторую — руководитель
 * (принять результат), и один код на двоих отправлял бы ждать не того человека.
 */
export const ROUTE_SHEET_BLOCKING_REASONS = [
  'ISSUE_MISSING',
  'ROUTE_NOT_FINISHED',
  'ROUTE_NOT_CONFIRMED',
  'MANUAL_MARK_NOT_APPROVED',
] as const;
export const RouteSheetBlockingReasonCodeSchema = z.enum(ROUTE_SHEET_BLOCKING_REASONS);
export type RouteSheetBlockingReasonCode = z.infer<typeof RouteSheetBlockingReasonCodeSchema>;

export const RouteSheetBlockingReasonSchema = z.object({
  code: RouteSheetBlockingReasonCodeSchema,
  /** Поездка, о которой речь. NULL только у `ISSUE_MISSING`. */
  routeId: z.string().nullable(),
  /** Чем поездку назвать на экране: станция работ и назначенный выезд. */
  stationName: z.string().nullable(),
  plannedDepartureAt: z.string().nullable(),
});
export type RouteSheetBlockingReason = z.infer<typeof RouteSheetBlockingReasonSchema>;

/** Шапка листа (ADR-008 §6). Номер пуст, пока лист не выдан. */
export const RouteSheetHeaderSchema = z.object({
  number: z.string().nullable(),
  month: RouteSheetMonthSchema,
  organizationCode: z.string(),
  organizationName: z.string(),
  subdivisionName: z.string().nullable(),
  /** Должность по бланку — профессия из справочника ЕТКС. */
  professionName: z.string().nullable(),
  workerId: z.string(),
  workerName: z.string(),
  workerEmployeeNumber: z.string(),
  /** Место приписки с кодом станции. Пусто — в профиле оно не заполнено. */
  homeStationName: z.string().nullable(),
  homeStationEsrCode: z.string().nullable(),
});
export type RouteSheetHeader = z.infer<typeof RouteSheetHeaderSchema>;

/** Отметка о выдаче: «Выдал начальник цеха» (ADR-008 §7). */
export const RouteSheetIssueSchema = z.object({
  documentId: z.string(),
  hash: z.string(),
  /** Подписана ли выдача. Незаконченная выдача листа не открывает. */
  signed: z.boolean(),
  signedAt: z.string().nullable(),
  issuedByName: z.string(),
  issuedByEmployeeNumber: z.string(),
});
export type RouteSheetIssue = z.infer<typeof RouteSheetIssueSchema>;

/**
 * Роли трёх подписей над одними байтами листа (ADR-008 §7, issue #509), по
 * порядку: «сдал» работник, «подтверждаю» старший, «Утверждаю к оплате»
 * начальник.
 */
export const ROUTE_SHEET_SIGNER_ROLES = [
  'ROUTE_SHEET_SUBMITTER',
  'ROUTE_SHEET_CONFIRMER',
  'ROUTE_SHEET_APPROVER',
] as const;
export const RouteSheetSignerRoleSchema = z.enum(ROUTE_SHEET_SIGNER_ROLES);
export type RouteSheetSignerRole = z.infer<typeof RouteSheetSignerRoleSchema>;

/** Подпись под листом: кто, когда и чем (ключ телефона или ЭЦП). */
export const RouteSheetSignatureSchema = z.object({
  role: RouteSheetSignerRoleSchema,
  name: z.string(),
  employeeNumber: z.string(),
  signedAt: z.string(),
  level: z.enum(['INTERNAL_DEVICE', 'QUALIFIED_ECP']),
});
export type RouteSheetSignature = z.infer<typeof RouteSheetSignatureSchema>;

/**
 * Сданный экземпляр листа: документ `ROUTE_SHEET` и подписи под ним.
 *
 * Строки экземпляра заморожены в момент сдачи и лежат в его байтах; строки,
 * которые показывает `lines`, — это лист, собранный из поездок СЕЙЧАС. После
 * сдачи они совпадают: поездки сданного листа никто не правит.
 */
export const RouteSheetSubmissionSchema = z.object({
  documentId: z.string(),
  hash: z.string(),
  signatures: z.array(RouteSheetSignatureSchema),
});
export type RouteSheetSubmission = z.infer<typeof RouteSheetSubmissionSchema>;

/**
 * Возврат на исправление (решение владельца В9): кто вернул, почему и какие
 * поездки надо исправить. Возвращённый экземпляр остаётся в истории как есть.
 */
export const RouteSheetReturnViewSchema = z.object({
  documentId: z.string(),
  reason: z.string(),
  returnedAt: z.string(),
  returnedByName: z.string(),
  returnedByEmployeeNumber: z.string(),
  routeIds: z.array(z.string()),
});
export type RouteSheetReturnView = z.infer<typeof RouteSheetReturnViewSchema>;

/**
 * Маршрутный лист за месяц, как его показывают телефон и веб-панель.
 *
 * Лист СОБИРАЕТСЯ, а не заполняется: строки выводятся из поездок, и второй
 * копии их в базе нет (ADR-008 §1, §6). Поэтому месяц без выдачи и без единой
 * поездки — законный ответ, а не «лист не найден»: поездки месяца копятся, и
 * ездить можно без выдачи (решение владельца В11).
 */
export const RouteSheetViewSchema = z.object({
  /** Строка `RouteSheet`. NULL — лист ещё не заводили: выдачи не было. */
  id: z.string().nullable(),
  month: RouteSheetMonthSchema,
  status: z.enum(['OPEN', 'SUBMITTED', 'CONFIRMED', 'APPROVED', 'RETURNED']),
  header: RouteSheetHeaderSchema,
  issue: RouteSheetIssueSchema.nullable(),
  lines: z.array(RouteSheetLineSchema),
  blockingReasons: z.array(RouteSheetBlockingReasonSchema),
  /** Пустой перечень причин — и только он — открывает сдачу (issue #509). */
  canSubmit: z.boolean(),
  /**
   * Живой сданный экземпляр: под ним стоит хотя бы подпись работника, и его
   * не возвращали. NULL — лист не сдан или возвращён.
   */
  submission: RouteSheetSubmissionSchema.nullable(),
  /** Возвраты на исправление, последний — первым. */
  returns: z.array(RouteSheetReturnViewSchema),
  /**
   * Поездки, открытые на исправление последним возвратом: пока лист
   * возвращён, работник правит именно их. Пусто — открытых поездок нет.
   */
  reopenedRouteIds: z.array(z.string()),
  /**
   * Сдача раньше последнего дня месяца (решение владельца 27.09.2026): сколько
   * дней осталось. Поездки после сдачи в этот лист не попадут, пока его не
   * вернут, и сдача требует подтверждения. NULL — месяц кончился или сегодня
   * его последний день.
   */
  earlySubmission: z.object({ daysLeft: z.number().int().positive(), lastDay: z.string() }).nullable(),
});
export type RouteSheetView = z.infer<typeof RouteSheetViewSchema>;

/**
 * Выдать лист работнику на месяц. Номер присваивает сервер (ADR-008 §6) —
 * клиент его не присылает и прислать не может: он его ещё не знает.
 */
export const IssueRouteSheetSchema = z.object({
  workerId: z.string().uuid(),
  month: RouteSheetMonthSchema,
});
export type IssueRouteSheetDto = z.infer<typeof IssueRouteSheetSchema>;

/**
 * Ответ на выдачу: лист с присвоенным номером и БАЙТЫ документа выдачи, под
 * которыми начальнику цеха предстоит расписаться.
 *
 * Байты отдаются сразу, а не отдельным запросом: подписывать нужно ровно то,
 * что сервер зафиксировал, и лишний круг между присвоением номера и подписью
 * создал бы щель, в которой телефон подписал бы не тот снимок.
 */
export const RouteSheetIssueRequestSchema = z.object({
  sheet: RouteSheetViewSchema,
  documentId: z.string(),
  canonicalJson: z.string(),
  hash: z.string(),
  /** true — выдача уже была зафиксирована раньше и возвращена как есть. */
  existing: z.boolean(),
});
export type RouteSheetIssueRequest = z.infer<typeof RouteSheetIssueRequestSchema>;

/**
 * Подпись выдачи ключом телефона (ADR-008 §7, уровень `INTERNAL_DEVICE`).
 *
 * Канона в теле НЕТ, и это не упущение: подписываются байты, которые сервер
 * уже отдал в ответе на выдачу. Сверять их с присланными значило бы спрашивать
 * подписанта, что он подписал, — тем же правилом живёт подпись мероприятий
 * пункта 7 бланка № 344 (`mitigation-signatures.service.ts`).
 */
export const SignRouteSheetIssueSchema = z.object({
  deviceId: z.string().min(1),
  signature: z.string().min(1),
  unlockMethod: z.enum(['BIOMETRIC', 'DEVICE_CREDENTIAL']),
});
export type SignRouteSheetIssueDto = z.infer<typeof SignRouteSheetIssueSchema>;

/**
 * Сдача листа: сервер собирает экземпляр из поездок и отдаёт его БАЙТЫ, под
 * которыми работник расписывается ключом телефона (ADR-008 §7, issue #509).
 * Строки замораживаются в момент подписи: байты те, что сервер отдал здесь.
 */
/**
 * Тело сдачи. `acknowledgeEarly` — человек подтвердил, что сдаёт раньше конца
 * месяца и знает: поездки после сдачи в этот лист не попадут, пока его не
 * вернут. Без подтверждения ранняя сдача отказывает.
 */
export const SubmitRouteSheetSchema = z
  .object({ acknowledgeEarly: z.boolean().optional() })
  .optional()
  .transform((value) => value ?? {});
export type SubmitRouteSheetDto = z.infer<typeof SubmitRouteSheetSchema>;

/** Лист, ждущий «Утверждаю к оплате», — в перечне руководителя дистанции. */
export const RouteSheetAwaitingApprovalSchema = z.object({
  sheetId: z.string(),
  month: RouteSheetMonthSchema,
  number: z.string().nullable(),
  workerId: z.string(),
  workerName: z.string(),
  workerEmployeeNumber: z.string(),
});
export type RouteSheetAwaitingApproval = z.infer<typeof RouteSheetAwaitingApprovalSchema>;

// ─── Issue #510: раздел «Маршрутные листы» и форма ФРУ № 12 для бухгалтерии ──

/** Состояние листа в перечне раздела (ADR-008 §7). */
export const RouteSheetStatusSchema = z.enum(['OPEN', 'SUBMITTED', 'CONFIRMED', 'APPROVED', 'RETURNED']);
export type RouteSheetStatus = z.infer<typeof RouteSheetStatusSchema>;

/**
 * Строка перечня листов за месяц. `workerName` — ФИО из карточки; NULL —
 * ФИО не заполнено: профессию на её место не подставляет никто.
 */
export const RouteSheetListItemSchema = z.object({
  sheetId: z.string(),
  month: RouteSheetMonthSchema,
  number: z.string().nullable(),
  status: RouteSheetStatusSchema,
  workerId: z.string(),
  workerName: z.string().nullable(),
  workerEmployeeNumber: z.string(),
  subdivisionName: z.string().nullable(),
});
export type RouteSheetListItem = z.infer<typeof RouteSheetListItemSchema>;

export const RouteSheetListResponseSchema = z.object({
  month: RouteSheetMonthSchema,
  sheets: z.array(RouteSheetListItemSchema),
});
export type RouteSheetListResponse = z.infer<typeof RouteSheetListResponseSchema>;

/** Подпись в графе формы: кто, когда и чем (ключ телефона или ЭЦП). */
export const RouteSheetFormSignatureSchema = z.object({
  name: z.string(),
  employeeNumber: z.string(),
  signedAt: z.string(),
  level: z.enum(['INTERNAL_DEVICE', 'QUALIFIED_ECP']),
});
export type RouteSheetFormSignature = z.infer<typeof RouteSheetFormSignatureSchema>;

/** Графа «Выбыл» или «Прибыл» формы: дата и время по Казахстану, как на бланке. */
export const RouteSheetFormMarkSchema = z.object({
  date: z.string(),
  time: z.string(),
  trainNumber: z.string().nullable(),
  stationName: z.string().nullable(),
  stationEsrCode: z.string().nullable(),
  /** QR — отметка по коду станции; ручная помечается и называет подтвердившего. */
  method: z.enum(['QR', 'MANUAL_FALLBACK']),
  manualApprovedByName: z.string().nullable(),
  manualApprovedByEmployeeNumber: z.string().nullable(),
});
export type RouteSheetFormMark = z.infer<typeof RouteSheetFormMarkSchema>;

export const RouteSheetFormLineSchema = z.object({
  sequence: z.number().int(),
  departed: RouteSheetFormMarkSchema,
  arrived: RouteSheetFormMarkSchema,
  workName: z.string().nullable(),
  resultStatus: z.enum(['DONE', 'PARTIAL', 'NOT_DONE']).nullable(),
  resultComment: z.string().nullable(),
  returnToHome: z.boolean(),
});
export type RouteSheetFormLine = z.infer<typeof RouteSheetFormLineSchema>;

/**
 * Лист в виде бланка ФРУ № 12 (ADR-008 §9, issue #510).
 *
 * Утверждённый к оплате собирается из БАЙТОВ утверждённого экземпляра, а не
 * из поездок сейчас: бухгалтерия получает ровно то, под чем стоят три подписи.
 *
 * Неутверждённый тоже печатается и выгружается (решение владельца по PR #593),
 * но с пометкой «Не утверждён к оплате» (`approvedForPayment: false`): сданный
 * — из байтов сданного экземпляра с теми подписями, что под ним уже есть;
 * ещё не сданный — из строк листа сейчас, без подписей и без документа.
 */
export const RouteSheetFormSchema = z.object({
  sheetId: z.string(),
  /**
   * Утверждён ли лист к оплате (ЭЦП «Утверждаю к оплате»). `false` — бланк
   * печатается и выгружается с пометкой «Не утверждён к оплате».
   */
  approvedForPayment: z.boolean(),
  /**
   * Документ экземпляра и его хеш — для сверки подписанного. NULL — лист ещё
   * не сдан, экземпляра нет: бланк собран из строк листа сейчас.
   */
  documentId: z.string().nullable(),
  hash: z.string().nullable(),
  number: z.string().nullable(),
  month: RouteSheetMonthSchema,
  organizationName: z.string(),
  subdivisionName: z.string().nullable(),
  /** Должность по бланку — профессия из справочника ЕТКС. */
  position: z.string().nullable(),
  /** ФИО; NULL — не заполнено (форма пишет «ФИО не заполнено»). */
  workerFullName: z.string().nullable(),
  workerEmployeeNumber: z.string(),
  homeStationName: z.string().nullable(),
  homeStationEsrCode: z.string().nullable(),
  lines: z.array(RouteSheetFormLineSchema),
  /** «Выдал начальник цеха» — отметка о выдаче ключом телефона. */
  issued: RouteSheetFormSignatureSchema.nullable(),
  /** «Сдал» — работник, ключом телефона. NULL — не подписано (лист не утверждён). */
  submitted: RouteSheetFormSignatureSchema.nullable(),
  /** «Подтверждаю» — старший, ключом телефона. NULL — не подписано (лист не утверждён). */
  confirmed: RouteSheetFormSignatureSchema.nullable(),
  /** «Утверждаю к оплате» — начальник, ЭЦП. NULL — лист не утверждён к оплате. */
  approved: RouteSheetFormSignatureSchema.nullable(),
});
export type RouteSheetForm = z.infer<typeof RouteSheetFormSchema>;

export const RouteSheetSubmitRequestSchema = z.object({
  sheet: RouteSheetViewSchema,
  documentId: z.string(),
  canonicalJson: z.string(),
  hash: z.string(),
});
export type RouteSheetSubmitRequest = z.infer<typeof RouteSheetSubmitRequestSchema>;

/**
 * Подпись под листом ключом телефона — «сдал» работника или «подтверждаю»
 * старшего. Канона в теле нет по той же причине, что у подписи выдачи:
 * подписываются байты, которые сервер уже хранит.
 */
export const SignRouteSheetSchema = SignRouteSheetIssueSchema;
export type SignRouteSheetDto = z.infer<typeof SignRouteSheetSchema>;

/**
 * Возврат сданного листа работнику на исправление (решение владельца В9).
 * Причина обязательна, и поездки, которые надо исправить, названы поимённо:
 * именно они снова открываются работнику.
 */
export const ReturnRouteSheetSchema = z.object({
  reason: z.string().trim().min(1, 'Причина возврата обязательна').max(1000),
  routeIds: z.array(z.string().uuid()).min(1, 'Назовите поездки, которые надо исправить').max(100),
});
export type ReturnRouteSheetDto = z.infer<typeof ReturnRouteSheetSchema>;

// Type-only compatibility projections for the rest of the existing RAIL domain.
// They erase at runtime, avoiding a circular dependency with the root barrel.
export type {
  Station,
  CreateStationDto,
  UpdateStationDto,
  RailSection,
  Route,
  RouteEvent,
  RouteStation,
  RouteStationAssignment,
  RouteJournalEntry,
  RouteJournalKind,
  RoutePurpose,
  RouteStatus,
  Transport,
  ResultStatus,
  StatsTimeQuery,
  StatsTimeResponse,
  WorkerAdmin as RailWorkerProjection,
  CreateWorkerDto as CreateRailWorkerProjectionDto,
  UpdateWorkerDto as UpdateRailWorkerProjectionDto,
  WorkOrder as RailWorkOrderProjection,
  CreateWorkOrderDto as CreateRailWorkOrderProjectionDto,
} from './index';

export * from "./onboard";
